import React, { createContext, useContext, useEffect, useState } from 'react';
import { useFirebase } from './FirebaseContext';
import { signInWithGoogle, logout, loginWithEmail, registerWithEmail, updateAuthProfile, sendPasswordResetEmail, db } from '../firebase';
import { doc, updateDoc, setDoc } from 'firebase/firestore';
import { UserProfile } from '../types';

interface UserContextType {
  user: any | null;
  profile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
  isMember: boolean;
  dbConnected: boolean;
  signIn: (email?: string, password?: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  passwordLogin: (password: string) => Promise<boolean>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user: firebaseUser, loading: firebaseLoading, userProfile: firebaseProfile, isAdmin: firebaseIsAdmin, dbConnected } = useFirebase();
  const [user, setUser] = useState<any | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLocalAdmin, setIsLocalAdmin] = useState(() => {
    try {
      return localStorage.getItem('ows_admin_session') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!firebaseLoading) {
      setUser(firebaseUser);
      setProfile(firebaseProfile);
      setLoading(false);
    }
  }, [firebaseUser, firebaseLoading, firebaseProfile]);

  const signIn = async (email?: string, password?: string) => {
    if (!email || !password) return;
    setLoading(true);
    try {
      await loginWithEmail(email, password);
      setIsLocalAdmin(false);
      localStorage.removeItem('ows_admin_session');
    } catch (e: any) {
      if (!e.code?.includes('auth/')) {
        console.error("UserContext: Sign in failed", e);
      }
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    setLoading(true);
    try {
      await registerWithEmail(email, password);
      await updateAuthProfile({ displayName });
      setIsLocalAdmin(false);
      localStorage.removeItem('ows_admin_session');
    } catch (e: any) {
      if (!e.code?.includes('auth/')) {
        console.error("UserContext: Sign up failed", e);
      }
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const handleSignInWithGoogle = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (e: any) {
      if (e.code === 'auth/popup-closed-by-user' || e.message?.includes('popup-closed-by-user')) {
        console.warn("UserContext: Sign-in popup closed by user.");
        return; // Silent return for user closure
      }
      console.error("UserContext: Google Sign in failed", e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user && !isLocalAdmin) return;
    try {
      if (user) {
        await setDoc(doc(db, 'users', user.uid), updates, { merge: true });
      }
    } catch (e) {
      console.error("UserContext: Profile update failed", e);
    }
  };

  const signOut = async () => {
    await logout();
    localStorage.removeItem('ows_token');
    localStorage.removeItem('ows_admin_session');
    setUser(null);
    setProfile(null);
    setIsLocalAdmin(false);
  };

  const passwordLogin = async (password: string) => {
    const trimmedPassword = password.trim();
    if (trimmedPassword === 'ows2026') {
      setIsLocalAdmin(true);
      localStorage.setItem('ows_admin_session', 'true');
      return true;
    }
    return false;
  };

  const resetPassword = async (email: string) => {
    if (!email) throw new Error('Email is required');
    await sendPasswordResetEmail(email);
  };

  const isAdmin = firebaseIsAdmin || profile?.role === 'admin' || isLocalAdmin || firebaseUser?.email?.toLowerCase() === 'tshark1975@gmail.com';
  const isMember = profile?.role === 'member' || isAdmin || firebaseUser?.email?.toLowerCase() === 'tshark1975@gmail.com';

  return (
    <UserContext.Provider value={{ 
      user: firebaseUser, 
      profile: firebaseProfile, 
      loading: firebaseLoading, 
      isAdmin, 
      isMember, 
      dbConnected,
      signIn, 
      signUp, 
      signInWithGoogle: handleSignInWithGoogle,
      signOut, 
      passwordLogin, 
      resetPassword,
      updateProfile 
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
